/**
 * Force relations and the vector sum of several forces.
 *
 * - Newton's second law, F = ma (OpenStax University Physics Vol. 1, §5.3);
 * - weight, W = mg — the gravitational force on a mass, 9.80665 N per kg at standard
 *   gravity;
 * - friction, f = μN, and f = μmg for a block on level ground (§6.2).
 *
 * Forces are magnitudes in newtons and directions measured counter-clockwise from the
 * positive x axis, so a net force is found by adding components: ΣFx = ΣF cos θ and
 * ΣFy = ΣF sin θ.
 */

import type {
  RelationDef,
  RelationError,
} from "@/lib/tool-kit/physics-mechanics/relation";
import {
  ACCELERATION,
  COEFFICIENT,
  FORCE,
  MASS,
} from "@/lib/tool-kit/physics-mechanics/units";

const fail = (error: string): RelationError => ({ error });

export type LawKey = "F" | "m" | "a";

export const SECOND_LAW: RelationDef<LawKey> = {
  keys: ["F", "m", "a"],
  symbols: { F: "F", m: "m", a: "a" },
  families: { F: FORCE, m: MASS, a: ACCELERATION },
  equation: "F = m × a",
  solvedFor: "F",
  rearranged: { F: "F = m × a", m: "m = F ÷ a", a: "a = F ÷ m" },
  solve(unknown, { F, m, a }) {
    if (unknown !== "m" && !(m > 0)) {
      return fail("mass-not-positive");
    }
    if (unknown === "F") {
      return m * a;
    }
    if (unknown === "a") {
      return F / m;
    }
    if (a === 0) {
      return fail("zero-acceleration");
    }
    const mass = F / a;
    return mass > 0 ? mass : fail("mass-sign");
  },
};

export type WeightKey = "W" | "m" | "g";

export const WEIGHT: RelationDef<WeightKey> = {
  keys: ["W", "m", "g"],
  symbols: { W: "W", m: "m", g: "g" },
  families: { W: FORCE, m: MASS, g: ACCELERATION },
  equation: "W = m × g",
  solvedFor: "W",
  rearranged: { W: "W = m × g", m: "m = W ÷ g", g: "g = W ÷ m" },
  solve(unknown, { W, m, g }) {
    if (unknown !== "m" && !(m > 0)) {
      return fail("mass-not-positive");
    }
    if (unknown !== "g" && !(g > 0)) {
      return fail("gravity-not-positive");
    }
    if (unknown !== "W" && !(W > 0)) {
      return fail("weight-not-positive");
    }
    if (unknown === "W") {
      return m * g;
    }
    return unknown === "m" ? W / g : W / m;
  },
};

export type FrictionKey = "f" | "mu" | "N";

export const FRICTION: RelationDef<FrictionKey> = {
  keys: ["f", "mu", "N"],
  symbols: { f: "f", mu: "μ", N: "N" },
  families: { f: FORCE, mu: COEFFICIENT, N: FORCE },
  equation: "f = μ × N",
  solvedFor: "f",
  rearranged: { f: "f = μ × N", mu: "μ = f ÷ N", N: "N = f ÷ μ" },
  solve(unknown, { f, mu, N }) {
    if (unknown !== "mu" && mu < 0) {
      return fail("mu-negative");
    }
    if (unknown !== "N" && N < 0) {
      return fail("normal-negative");
    }
    if (unknown !== "f" && f < 0) {
      return fail("friction-negative");
    }
    if (unknown === "f") {
      return mu * N;
    }
    if (unknown === "mu") {
      return N > 0 ? f / N : fail("normal-zero");
    }
    return mu > 0 ? f / mu : fail("mu-zero");
  },
};

export type LevelFrictionKey = "f" | "mu" | "m" | "g";

export const FRICTION_LEVEL: RelationDef<LevelFrictionKey> = {
  keys: ["f", "mu", "m", "g"],
  symbols: { f: "f", mu: "μ", m: "m", g: "g" },
  families: { f: FORCE, mu: COEFFICIENT, m: MASS, g: ACCELERATION },
  equation: "f = μ × m × g",
  solvedFor: "f",
  rearranged: {
    f: "f = μ × m × g",
    mu: "μ = f ÷ (m × g)",
    m: "m = f ÷ (μ × g)",
    g: "g = f ÷ (μ × m)",
  },
  solve(unknown, { f, mu, m, g }) {
    if (unknown !== "mu" && mu < 0) {
      return fail("mu-negative");
    }
    if (unknown !== "m" && !(m > 0)) {
      return fail("mass-not-positive");
    }
    if (unknown !== "g" && !(g > 0)) {
      return fail("gravity-not-positive");
    }
    if (unknown !== "f" && f < 0) {
      return fail("friction-negative");
    }
    if (unknown === "f") {
      return mu * m * g;
    }
    if (unknown === "mu") {
      return f / (m * g);
    }
    if (!(mu > 0)) {
      return fail("mu-zero");
    }
    return unknown === "m" ? f / (mu * g) : f / (mu * m);
  },
};

export interface ForceVector {
  /** Newtons, ≥ 0. */
  magnitude: number;
  /** Radians, counter-clockwise from the +x axis. */
  angle: number;
}

export interface NetForce {
  components: { fx: number; fy: number }[];
  fx: number;
  fy: number;
  magnitude: number;
  /** Direction of the net force in radians, 0 to 2π; null when the forces balance. */
  angle: number | null;
  /** True when the forces cancel (the object is in translational equilibrium). */
  balanced: boolean;
}

/** A value so small next to the forces involved that it is rounding, not physics. */
function snap(value: number, scale: number): number {
  return Math.abs(value) <= 1e-12 * scale ? 0 : value;
}

export function netForce(forces: readonly ForceVector[]): NetForce {
  const scale = forces.reduce((sum, force) => sum + force.magnitude, 0);
  const components = forces.map((force) => ({
    fx: snap(force.magnitude * Math.cos(force.angle), scale),
    fy: snap(force.magnitude * Math.sin(force.angle), scale),
  }));
  const fx = snap(
    components.reduce((sum, c) => sum + c.fx, 0),
    scale
  );
  const fy = snap(
    components.reduce((sum, c) => sum + c.fy, 0),
    scale
  );
  const magnitude = Math.hypot(fx, fy);
  const balanced = magnitude <= 1e-9 * Math.max(scale, 1e-300);
  const raw = Math.atan2(fy, fx);
  return {
    components,
    fx,
    fy,
    magnitude: balanced ? 0 : magnitude,
    angle: balanced ? null : (raw + 2 * Math.PI) % (2 * Math.PI),
    balanced,
  };
}
